import { useRef, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import api from "utils/api";
import { toast } from "react-toastify";

function CsvBulkUploadDialog({
  open,
  onClose,
  title,
  endpoint,
  description,
  templateUrl,
  onSuccess,
}) {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const resetAndClose = () => {
    setSelectedFile(null);
    setResult(null);
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a CSV file");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a CSV file");
      return;
    }

    const formData = new FormData();
    formData.append("csvFile", selectedFile);

    try {
      setLoading(true);
      const response = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = response.data?.data || response.data;
      setResult(data);

      if (response.data?.success) {
        toast.success(response.data?.message || "CSV uploaded successfully");
        onSuccess?.();
      } else {
        toast.warn(response.data?.message || "CSV processed with issues");
      }
    } catch (error) {
      const data = error.response?.data?.data || error.response?.data;
      setResult(data || { errors: [error.response?.data?.message || error.message] });
      toast.error(error.response?.data?.message || "CSV upload failed");
    } finally {
      setLoading(false);
    }
  };

  const errors = result?.errors || result?.failedRows || [];

  return (
    <Dialog open={open} onClose={loading ? undefined : resetAndClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <CloudUploadIcon color="info" />
            <ArgonTypography variant="h5" fontWeight="bold">
              {title}
            </ArgonTypography>
          </ArgonBox>
          <IconButton onClick={resetAndClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </ArgonBox>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {description}
        </Alert>

        {/* {templateUrl && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <ArgonTypography variant="caption" display="block" mb={1}>
              Download the flattened CSV template before uploading.
            </ArgonTypography>
            <ArgonButton
              variant="outlined"
              color="info"
              onClick={() => window.open(templateUrl, "_blank", "noopener,noreferrer")}
            >
              Download Template
            </ArgonButton>
          </Alert>
        )} */}

        <ArgonBox
          onClick={() => inputRef.current?.click()}
          sx={{
            border: "1px dashed #8aa4d6",
            borderRadius: "8px",
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: "#f8fbff",
          }}
        >
          <CloudUploadIcon color="info" />
          <ArgonTypography variant="button" display="block" fontWeight="bold">
            Click here to select a csv file
          </ArgonTypography>
          <input ref={inputRef} hidden type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </ArgonBox>

        {selectedFile && (
          <ArgonBox display="flex" alignItems="center" gap={1} mt={2}>
            <InsertDriveFileIcon fontSize="small" />
            <ArgonTypography variant="caption">{selectedFile.name}</ArgonTypography>
          </ArgonBox>
        )}

        {result && (
          <ArgonBox mt={2}>
            <ArgonBox display="flex" gap={1} flexWrap="wrap" mb={errors.length ? 1 : 0}>
              <Alert severity="info">Rows: {result.totalRows ?? result.rows ?? 0}</Alert>
              <Alert severity="success">Success: {result.successCount ?? 0}</Alert>
              <Alert severity={errors.length ? "warning" : "success"}>Failed: {result.failureCount ?? errors.length}</Alert>
            </ArgonBox>
            {errors.length > 0 && (
              <Alert severity="error">
                {errors.slice(0, 5).map((error, index) => (
                  <ArgonTypography key={index} variant="caption" display="block">
                    {typeof error === "string" ? error : error.message || JSON.stringify(error)}
                  </ArgonTypography>
                ))}
              </Alert>
            )}
          </ArgonBox>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <ArgonButton variant="outlined" color="secondary" onClick={resetAndClose} disabled={loading}>
          Close
        </ArgonButton>
        <ArgonButton variant="gradient" color="info" onClick={handleUpload} disabled={loading}>
          {loading ? "Uploading..." : "Upload CSV"}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
}

CsvBulkUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  templateUrl: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default CsvBulkUploadDialog;
